import { useSyncExternalStore } from 'react';

/**
 * Which app (by registry slug) is currently confirmed DOWN, or null.
 *
 * A tiny external store rather than context because the two parties sit on
 * opposite sides of a `<Outlet/>`: {@link AppRoute} (routed child) is what
 * confirms the outage via `/system/status/:app`, while `AppShellLayout` (the
 * parent layout) is what renders the rail and needs to know. Passing it down
 * would mean threading a setter through the router tree.
 *
 * Why the rail cares: rail items are permission-gated and FAIL CLOSED until the
 * shell has that app's permission map, fetched from the app's own api. When the api
 * is down the map never arrives, so every gated item is filtered out and the
 * sidebar collapses to just the ungated entries — which reads as "you lost your
 * permissions" rather than "this app's backend is down". On a confirmed outage
 * the layout instead renders the app's FULL rail, visually disabled.
 */
let outageSlug: string | null = null;
const listeners = new Set<() => void>();

/** Publish (or clear, with `null`) the app currently known to be down. */
export function setAppOutage(slug: string | null): void {
  if (outageSlug === slug) return;
  outageSlug = slug;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): string | null {
  return outageSlug;
}

/** Slug of the app currently confirmed down, or null. */
export function useAppOutage(): string | null {
  // Same snapshot on the server — nothing is down before hydration.
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}
